import { FormikHelpers, Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { Card, CardHeader, CardContent, makeStyles } from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import { useSubaccount } from "src/components/SubaccountProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import { Role } from "src/graphql/generated/globalTypes";
import {
  subaccountEditSettings,
  subaccountEditSettingsVariables
} from "src/graphql/generated/subaccountEditSettings";
import { EDIT_SUBACCOUNT_SETTINGS } from "src/graphql/subaccount";
import { hasRole } from "src/util/roles";
import useMutation from "src/util/useMutation";

type Values = {
  name: string;
  callbackNumber: string;
  collectCallerEmail: boolean;
  restrictExports: boolean;
  captchaRequired: boolean;
};

const useStyles = makeStyles(theme => ({
  cardSubsection: {
    marginBottom: theme.spacing(4)
  },
  cardSubsectionHeader: {
    marginBottom: theme.spacing(1)
  }
}));

export default function SubaccountSettings(): JSX.Element {
  const tenant = useTenant()!;
  const entityName = tenant.isUsingSubaccounts ? "Subaccount" : "Organization";
  const { subaccount, membership } = useSubaccount()!;
  const snackbar = useSnackbar();
  const classes = useStyles();

  const schema = yup.object().shape({
    name: yup.string().required("Required"),
    callbackNumber: yup.string().phoneNumber(tenant.countryIso),
    collectCallerEmail: yup.bool(),
    restrictExports: yup.bool(),
    captchaRequired: yup.bool()
  });

  const [editSubaccountSettings] = useMutation<
    subaccountEditSettings,
    subaccountEditSettingsVariables
  >(EDIT_SUBACCOUNT_SETTINGS, {
    onErrorSnackbarMessage: `Failed to update ${entityName} settings.`
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    const { errors, data } = await editSubaccountSettings({
      variables: {
        name: values.name,
        callbackNumber: values.callbackNumber || null,
        collectCallerEmail: values.collectCallerEmail,
        restrictExports: values.restrictExports,
        captchaRequired: values.captchaRequired,
        subaccountId: subaccount.id
      }
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Organization updated.", { variant: "info" });
      helpers.resetForm({
        values: {
          name: data!.subaccountEditName.name,
          callbackNumber:
            data!.subaccountEditCallbackNumber.settings.callbackNumber || "",
          collectCallerEmail:
            data!.subaccountEditCollectCallerEmail.settings.collectCallerEmail,
          restrictExports:
            data!.subaccountEditRestrictExports.settings.restrictExports,
          captchaRequired:
            data!.subaccountEditCaptchaRequired.settings.captchaRequired
        }
      });
    }
  }

  return (
    <Card>
      <CardHeader title={`${entityName} Settings`} />
      <CardContent>
        <Formik
          initialValues={{
            name: subaccount.name,
            callbackNumber: subaccount.settings.callbackNumber || "",
            collectCallerEmail: subaccount.settings.collectCallerEmail,
            restrictExports: subaccount.settings.restrictExports,
            captchaRequired: subaccount.settings.captchaRequired
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label={`${entityName} Name`}
                name="name"
                disabled={isSubmitting}
              />
              <FormikTextField
                fullWidth
                label="Callback Forwarding Number"
                helperText="Enter a number to forward calls to when a contact calls back. Leave blank to drop these calls."
                name="callbackNumber"
                disabled={isSubmitting}
              />

              <div className={classes.cardSubsection}>
                <h4 className={classes.cardSubsectionHeader}>
                  Caller UI Settings
                </h4>
                <FormikSwitchField
                  label="Collect email from callers"
                  helperText="Require callers to provide an email address (in addition to name and phone number) when using “Call with computer” or “Call with computer & phone” mode"
                  name="collectCallerEmail"
                  disabled={isSubmitting}
                />
                <FormikSwitchField
                  label="CAPTCHA required"
                  helperText="Require callers to complete a CAPTCHA to prevent bots"
                  name="captchaRequired"
                  disabled={isSubmitting}
                />
              </div>

              {hasRole(membership, Role.OWNER) && (
                <div className={classes.cardSubsection}>
                  <h4 className={classes.cardSubsectionHeader}>
                    Admin Permissions
                  </h4>
                  <FormikSwitchField
                    label="Restrict campaign exports to organization owners"
                    helperText="Only organization owners will be allowed to export a csv of campaign results"
                    name="restrictExports"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              <SubmitButton
                type="submit"
                disabled={!dirty}
                isSubmitting={isSubmitting}
                submitText="Update"
                loadingText="Updating..."
              />
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}
